import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { SnackbarSeverity } from '@/stores/snackbarStore';

type ShowSnackbarFn = (
  message: string,
  severity?: SnackbarSeverity,
  autoHideDuration?: number | null,
  title?: string
) => void;

export type SearchErrorContext = {
  /**
   * True when the user clicked Search/Apply on the panel.
   * False when the grid auto-searched (e.g. persisted filters after page load).
   */
  userInitiatedSearch?: boolean;
  /** Configured bug-report URL (system config); appended as plain text when present. */
  bugReportUrl?: string;
  /** Data manager email from system config; used when no bug-report URL is configured. */
  dataManagerEmail?: string;
};

/** Follow-up when the user should escalate after reload (plain text — snackbars are not HTML). */
function formatPersistErrorSuffix(bugReportUrl?: string, dataManagerEmail?: string): string {
  if (bugReportUrl) {
    return ` If the problem persists, report a bug: ${bugReportUrl}`;
  }

  const email = dataManagerEmail?.trim();
  if (email) {
    return ` If the problem persists, contact the data manager at ${email}.`;
  }

  return ' If the problem persists, contact the data manager.';
}

function buildUserInitiated400Message(
  baseMessage: string,
  detailedMessage: string,
  bugReportUrl?: string,
  dataManagerEmail?: string
): string {
  const intro = `${baseMessage} with HTTP 400 after you clicked Search. This should not happen — it may be a bug, or the server was updated before this client. Try reloading the page to resync the UI with the server.`;
  return `${intro}${formatPersistErrorSuffix(bugReportUrl, dataManagerEmail)}${detailedMessage}`;
}

/**
 * Normalized handler for search errors coming from backend calls triggered by table search POSTs.
 */
export function handleSearchError(
  error: unknown,
  showSnackbar: ShowSnackbarFn,
  contextLabel: string,
  context?: SearchErrorContext
): void {
  // HTTP status lives on ApiException.status.
  const status: number | undefined =
    error && typeof error === 'object' && 'status' in error && typeof (error as { status: unknown }).status === 'number'
      ? (error as { status: number }).status
      : undefined;

  const baseMessage = `Search for ${contextLabel} failed`;

  const backendMessage = getProblemDetail(error);
  const detailedMessage = backendMessage ? ` Details: ${backendMessage}` : '';

  if (status === 400) {
    if (context?.userInitiatedSearch) {
      const message = buildUserInitiated400Message(
        baseMessage,
        detailedMessage,
        context.bugReportUrl,
        context.dataManagerEmail
      );
      log.error('[SearchErrorHandler] Search HTTP 400 (user-initiated)', { contextLabel, error });
      showSnackbar(message, 'error', null, 'Search error');
      return;
    }

    const fullMessage = `${baseMessage}. Your saved filters may be out of date after a LiST update. The filter panel has been reopened — review your criteria, use Reset if needed, then search again.${detailedMessage}`;
    log.error('[SearchErrorHandler] Search HTTP 400 (hydration)', { contextLabel, error });
    showSnackbar(fullMessage, 'error', null, 'Search error');
    return;
  }

  if (status === 401 || status === 403) {
    const classification = 'authorization error (authentication/authorization misconfiguration)';
    const fullMessage = `${baseMessage} with HTTP ${status} (${classification}). This should not happen; please report this to the LiST team.${detailedMessage}`;
    log.error('[SearchErrorHandler] Unexpected search auth error', { contextLabel, status, error });
    showSnackbar(fullMessage, 'error', null, 'Search error');
    return;
  }

  if (status === 500) {
    const fullMessage = `${baseMessage} with HTTP 500 (internal error). This should not happen; please report this to the LiST team.${detailedMessage}`;
    log.error('[SearchErrorHandler] Unexpected search server error', { contextLabel, status, error });
    showSnackbar(fullMessage, 'error', null, 'Search error');
    return;
  }

  log.error('[SearchErrorHandler] Non-HTTP or unexpected search error', { contextLabel, error });
  showSnackbar(
    `${baseMessage}. An unexpected error occurred; please try again or contact the LiST team if this persists.`,
    'error',
    null,
    'Search error'
  );
}
